import { ComponentChild } from 'preact';
import { BaseOption } from 'types/options';
import { useUniqueId } from 'utilities/use-unique-id';

import { Text } from 'components/Text';

import { Item } from './components/Item';
import { useActionList } from './utilities';

import styles from './ActionList.module.scss';

interface ActionListProps {
  title?: string;
  activator: ComponentChild;
  options: BaseOption[];
  selected?: string;
  onChange: (value?: string) => void;
}

export function ActionList({
  title,
  activator,
  options,
  selected,
  onChange,
}: ActionListProps) {
  const id = useUniqueId('ActionList');
  const {
    open,
    close,
    reference,
    floating,
    referenceProps,
    floatingProps,
    floatingStyle,
  } = useActionList();

  const titleMarkup = title ? (
    <legend className={styles.Title}>
      <Text size="medium" variation="strong">
        {title}
      </Text>
    </legend>
  ) : null;

  const itemsMarkup = options.map(({ label, value }) => {
    const handleChange = (selected: boolean) => {
      onChange(selected ? value : undefined);
      close();
    };

    return (
      <Item
        key={value}
        selected={selected === value}
        label={label}
        value={value}
        onChange={handleChange}
      />
    );
  });

  const showDropdown = itemsMarkup.length > 0;

  if (!showDropdown) return null;

  return (
    <>
      <div ref={reference} {...referenceProps} className={styles.Activator}>
        {activator}
      </div>
      {open && (
        <div
          ref={floating}
          {...floatingProps}
          style={floatingStyle}
          className={styles.Card}
        >
          <fieldset className={styles.ActionList} id={id}>
            {titleMarkup}
            <ul className={styles.Items}>{itemsMarkup}</ul>
          </fieldset>
        </div>
      )}
    </>
  );
}
